import { Link } from "react-router-dom";
import { GAMES, CATEGORY_ORDER, FAMILY } from "@/lib/games";
import { formatTime24to12 } from "@/lib/format";

/**
 * SEO content block for the homepage bottom.
 * Every one of the 21 games is mentioned naturally with an internal link.
 * Nothing here promotes gambling or makes prediction claims.
 */
export default function HomeContent() {
  const grouped = CATEGORY_ORDER.map((cat) => ({
    cat,
    items: GAMES.filter((g) => g.category === cat),
  }));

  return (
    <article className="mt-6 border-t border-neutral-200 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 text-neutral-700 leading-relaxed text-[15px] sm:text-base space-y-8">
        <section>
          <h2 className="font-heading text-2xl sm:text-3xl font-black text-neutral-900">
            Juwai Teer Result — All 21 Teer Results in One Place
          </h2>
          <p className="mt-3">
            Juwai Teer is a central Teer result portal that has been serving visitors
            since <strong>2018</strong> and is still actively updated in <strong>{new Date().getFullYear()}</strong>.
            The homepage above shows the latest First Round (F/R) and Second Round (S/R) result cards for every
            Teer we track. Each card also links to that Teer&apos;s dedicated result page, its complete previous result
            history and its daily common number breakdown (Direct Number, House and Ending).
          </p>
          <p className="mt-3">
            All game times displayed on Juwai Teer are in <strong>IST (Asia/Kolkata)</strong>. The information on
            this page is provided strictly for informational and educational reference. Please cross-verify every
            number with your local official Teer counter.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-xl sm:text-2xl font-black text-neutral-900">
            All 21 Teer Results we cover
          </h2>
          <p className="mt-2">
            Every Teer has its own dedicated page containing today&apos;s result card, recent results, a filterable
            previous result table (by year and month) and its own common number page. Choose a Teer below to
            jump straight to it.
          </p>

          <div className="mt-4 space-y-6">
            {grouped.map(({ cat, items }) => (
              <div key={cat}>
                <h3 className="font-heading text-lg font-bold text-neutral-900">{cat}</h3>
                <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[14px]">
                  {items.map((g) => {
                    const f = FAMILY[g.color_family];
                    return (
                      <li key={g.slug} className="flex items-baseline gap-2">
                        <span className={`font-numbers ${f.num} text-[13px]`}>#{String(g.number).padStart(2, "0")}</span>
                        <Link to={`/${g.slug}`} className="font-semibold text-neutral-900 hover:underline">
                          {g.name}
                        </Link>
                        <span className="text-neutral-500 text-[12px]">
                          — FR {formatTime24to12(g.fr_time)}, SR {formatTime24to12(g.sr_time)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-heading text-xl sm:text-2xl font-black text-neutral-900">
            How Juwai Teer works
          </h2>
          <p className="mt-2">
            Teer is a traditional archery-based number system popular in Meghalaya, Assam and neighbouring regions.
            Each Teer has two rounds every day — the <strong>First Round (F/R)</strong> and the
            <strong> Second Round (S/R)</strong>. Once the arrows are counted at the official counter, a two-digit
            result is announced for each round. Juwai Teer simply publishes those announced numbers on this
            website so that visitors from anywhere can look them up quickly on a mobile phone.
          </p>
          <p className="mt-3">
            The <strong>Direct Number</strong>, <strong>House</strong> and <strong>Ending</strong> on the Common
            Number pages are simple derivations from the daily result — Direct Number is the full two-digit pick,
            House is the first (tens) digit and Ending is the last (units) digit. These are shown for reference
            only and are not predictions or guarantees.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-xl sm:text-2xl font-black text-neutral-900">Frequently asked questions</h2>
          <div className="mt-3 space-y-4">
            <div>
              <h3 className="font-semibold text-neutral-900">Where can I see today&apos;s Juwai Teer result?</h3>
              <p className="mt-1">
                The homepage above shows today&apos;s F/R and S/R for {" "}
                <Link to="/juwai-teer" className="text-red-600 hover:underline">Juwai Teer</Link> and all other 20
                Teer results as soon as the numbers are declared. You can also open the dedicated {" "}
                <Link to="/juwai-teer" className="text-red-600 hover:underline">Juwai Teer result page</Link> for
                a bigger view and recent history.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-neutral-900">Does Juwai Teer cover Shillong, Khanapara, Bhutan and Guwahati Teer?</h3>
              <p className="mt-1">
                Yes. We cover {" "}
                <Link to="/shillong-teer" className="text-blue-700 hover:underline">Shillong Teer</Link>, {" "}
                <Link to="/shillong-morning-teer" className="text-blue-700 hover:underline">Shillong Morning Teer</Link>, {" "}
                <Link to="/shillong-night-teer" className="text-blue-700 hover:underline">Shillong Night Teer</Link>, {" "}
                <Link to="/khanapara-teer" className="text-teal-700 hover:underline">Khanapara Teer</Link>, {" "}
                <Link to="/bhutan-teer" className="text-amber-700 hover:underline">Bhutan Teer</Link>, {" "}
                <Link to="/guwahati-morning-teer" className="text-red-600 hover:underline">Guwahati Morning Teer</Link>, {" "}
                <Link to="/guwahati-night-teer" className="text-neutral-900 hover:underline">Guwahati Night Teer</Link>{" "}
                and the morning &amp; night variants of each — all 21 Teer results in total.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-neutral-900">Where do I find the previous result for a specific Teer?</h3>
              <p className="mt-1">
                Every Teer card has a <em>Previous Result</em> button. It opens that Teer&apos;s own previous-result
                page with a searchable date table, year filter and month filter. Historical data is stored
                per-Teer so results never get mixed.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-neutral-900">What is a common number and how is it different from a result?</h3>
              <p className="mt-1">
                A common number is a reference breakdown of the daily result into Direct Number, House and Ending.
                It is an informational reading of the number, not a prediction. The actual result is what the
                Teer counter officially declares each day. See the {" "}
                <Link to="/juwai-teer/common-number" className="text-red-600 hover:underline">Juwai Teer common number</Link>{" "}
                page for a live example.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-neutral-900">Is Juwai Teer a betting or gambling website?</h3>
              <p className="mt-1">
                No. Juwai Teer <strong>does not accept money, deposits or wagers of any kind</strong>. We do not
                sell tickets, we do not run any Teer draw and we are not affiliated with any Teer counter or
                association. This website only <em>displays</em> the numbers that are publicly announced, purely
                for informational reference.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-neutral-900">Since when is Juwai Teer online?</h3>
              <p className="mt-1">
                Juwai Teer has been publishing Teer result information online since <strong>2018</strong>. The
                platform continues to be actively updated every day in <strong>{new Date().getFullYear()}</strong>.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="font-heading text-xl sm:text-2xl font-black text-neutral-900">A note on trust and safety</h2>
          <p className="mt-2">
            Juwai Teer is an independent Teer information portal. We follow standard community guidelines — no
            gambling instructions, no &ldquo;how to play&rdquo; content, no guaranteed prediction claims, no promotional
            content and no user-submitted content is displayed on this website. Access to this site is intended
            only for adults (18+) and only where local laws permit viewing such information. If viewing Teer
            result content is restricted in your region, please leave this website.
          </p>
        </section>
      </div>
    </article>
  );
}
